// Cortes por color (01/11/2026): junta las piezas que faltan cortar de los pedidos abiertos del
// taller, agrupadas por COLOR de acrílico → modelo → medida, para armar el archivo de corte láser
// de a una plancha de color. Puro (sin supabase) para poder probarlo.
//
// Qué entra: pedidos en el taller (nueva / falta insumo; los 'cortado' ya están cortados) y, dentro
// de cada uno, los ítems sin el tilde de cortado (`is_cut`). Familia: hoy solo ESTANTES (pedido
// del usuario); la lista de familias está en FAMILIES.
//
// Cómo se lee el color del nombre del ítem (TN / admin escriben distinto):
//   "Estante Aire (50 cm, Azul Translúcido)"              → medida 50 cm, color azul
//   "Estante Revistero Bolsillo – 70cm / Transparente"    → idem con guión y barra (ventas manuales)
//   "Set de 4 Estantes Labios (Mix colores: Estante 1: Rojo … + Estante 2: Azul …)" → una pieza por color, sin medida
//   "Set de 2 Estantes Labios (Monocromo: Rojo Translúcido)" → N piezas de ese color, sin medida
//   "Estante Vinilo Box" (sin paréntesis) → "Sin color en el pedido"

// cut_colors: en un ítem de varios colores (set mix) los colores ya cortados; el ítem pasa a is_cut
// cuando están todos (marcar desde Cortes por color, 02/11/2026).
export interface CutItem { name?: string; quantity?: number | string; is_cut?: boolean; cut_colors?: string[] }
export interface CutOrder { id: number; num: string; status: string; payment_status: string | null; products: CutItem[] | null }

export interface CutRef { orderId: number; idx: number; color: string }
export interface CutRow { model: string; size: string; qty: number; orders: { num: string; orderId: number; qty: number; unpaid: boolean; refs: CutRef[] }[]; fromSet: boolean }
export interface CutColor { color: string; swatch: string; total: number; rows: CutRow[] }

export const FAMILIES = [{ key: 'estantes', label: 'Estantes', re: /estante/i }];

// Color canónico + muestra para la UI. El orden importa (negro/fumé antes que "negro").
const COLORS: { re: RegExp; name: string; swatch: string }[] = [
  { re: /transparente|cristal/i, name: 'Transparente', swatch: 'color-mix(in oklab, #9ad 25%, #fff)' },
  { re: /fum[eé]/i, name: 'Negro / Fumé translúcido', swatch: '#3b3b3b' },
  { re: /negro/i, name: 'Negro translúcido', swatch: '#1f1f1f' },
  { re: /naranja/i, name: 'Naranja translúcido', swatch: '#f08a24' },
  { re: /amarillo/i, name: 'Amarillo translúcido', swatch: '#f2c230' },
  { re: /cobre/i, name: 'Cobre translúcido', swatch: '#b8733d' },
  { re: /azul/i, name: 'Azul translúcido', swatch: '#2f6fd6' },
  { re: /verde/i, name: 'Verde translúcido', swatch: '#3aa35a' },
  { re: /rojo/i, name: 'Rojo translúcido', swatch: '#d33a2c' },
  { re: /rosa/i, name: 'Rosa translúcido', swatch: '#e889b5' },
  { re: /violeta|lila/i, name: 'Violeta translúcido', swatch: '#8a5bd6' },
  { re: /tornasol|arco/i, name: 'Tornasolado', swatch: 'linear-gradient(135deg,#f9a,#9df,#bf9)' },
  { re: /blanco/i, name: 'Blanco', swatch: '#f4f4f4' },
];
export const NO_COLOR = 'Sin color en el pedido';
// Modelos que vienen en un solo color (el nombre del ítem no lo dice). Agregar acá los que falten.
const FIXED_COLOR: { re: RegExp; color: string }[] = [
  { re: /estante vinilo box/i, color: 'Transparente' }, // 01/11/2026: siempre transparente
];

function canonColor(raw: string): { name: string; swatch: string } {
  const c = COLORS.find(x => x.re.test(raw));
  return c ? { name: c.name, swatch: c.swatch } : { name: raw.trim() ? raw.trim() : NO_COLOR, swatch: '#ccc' };
}
const cleanModel = (m: string) => m.replace(/^Set de \d+\s+/i, '').replace(/^Estantes\b/i, 'Estante').replace(/\s+/g, ' ').trim();
const normSize = (s: string) => s.replace(/(\d)\s*cm/i, '$1 cm').trim();
const sizeKey = (s: string) => { const m = s.match(/(\d+)/); return m ? Number(m[1]) : 9999; };

interface Piece { color: string; swatch: string; model: string; size: string; qty: number; fromSet: boolean }

export function piecesOf(name: string, qty: number): Piece[] {
  const n = name.replace(/\[[^\]]*\]\s*/g, '').trim();
  const fixed = FIXED_COLOR.find(f => f.re.test(n));
  const mk = (color: string, model: string, size: string, q: number, fromSet = false): Piece => {
    const c = canonColor(fixed ? fixed.color : color); return { color: c.name, swatch: c.swatch, model: cleanModel(model), size: normSize(size), qty: q, fromSet };
  };
  const setN = Number((n.match(/^Set de (\d+)/i) || [])[1]) || 1;
  // Set mix: una pieza por "Estante k: color"
  if (/mix colores/i.test(n)) {
    const model = n.split('(')[0];
    const cols = n.match(/Estante\s*\d+\s*:\s*[^+)]+/gi) || [];
    if (cols.length) return cols.map(c => mk(c.replace(/Estante\s*\d+\s*:/i, ''), model, '', qty, true));
  }
  // Set monocromo: N piezas del color
  const mono = n.match(/monocromo\s*:\s*([^)]+)/i);
  if (mono) return [mk(mono[1], n.split(/\(|–/)[0], '', qty * setN, true)];
  // "Modelo (medida, color)" o "Modelo (color)"
  const par = n.match(/^(.+?)\s*\((.*)\)\s*$/);
  if (par) {
    const parts = par[2].split(',').map(s => s.trim()).filter(Boolean);
    const color = parts.length ? parts[parts.length - 1] : '';
    const size = parts.length > 1 ? parts.slice(0, -1).join(', ') : '';
    return [mk(color, par[1], size, qty)];
  }
  // "Modelo – medida / color" (ventas manuales)
  if (n.includes('–')) {
    const [model, rest] = n.split('–');
    const bits = rest.split('/').map(s => s.trim()).filter(Boolean);
    return [mk(bits.length ? bits[bits.length - 1] : '', model, bits.length > 1 ? bits[0] : '', qty)];
  }
  // Sin variante (ML / publicaciones viejas): intenta sacar medida y color sueltos
  const size = (n.match(/(\d+\s*cm)/i) || [])[1] || '';
  const col = COLORS.find(x => x.re.test(n));
  return [mk(col ? col.name : '', n.replace(/\s*\d+\s*cm.*$/i, ''), size, qty)];
}

export function cutsByColor(orders: CutOrder[], opts: { family?: string; includeUnpaid?: boolean } = {}): CutColor[] {
  const fam = FAMILIES.find(f => f.key === (opts.family || 'estantes')) || FAMILIES[0];
  const byColor = new Map<string, { swatch: string; rows: Map<string, CutRow> }>();
  for (const o of orders) {
    if (!['nueva', 'falta_insumo'].includes(o.status)) continue;
    const unpaid = o.payment_status !== 'paid';
    if (unpaid && opts.includeUnpaid === false) continue;
    (Array.isArray(o.products) ? o.products : []).forEach((it, idx) => {
      if (it.is_cut || !fam.re.test(it.name || '')) return;
      const q = Math.max(1, Number(it.quantity) || 1);
      for (const p of piecesOf(it.name || '', q)) {
        if ((it.cut_colors || []).includes(p.color)) continue;
        const ref = { orderId: o.id, idx, color: p.color };
        if (!byColor.has(p.color)) byColor.set(p.color, { swatch: p.swatch, rows: new Map() });
        const rows = byColor.get(p.color)!.rows;
        const key = `${p.model}|${p.size}|${p.fromSet}`;
        const r = rows.get(key) || { model: p.model, size: p.size, qty: 0, orders: [], fromSet: p.fromSet };
        r.qty += p.qty;
        const ex = r.orders.find(x => x.num === o.num);
        if (ex) { ex.qty += p.qty; ex.refs.push(ref); } else r.orders.push({ num: o.num, orderId: o.id, qty: p.qty, unpaid, refs: [ref] });
        rows.set(key, r);
      }
    });
  }
  return Array.from(byColor.entries()).map(([color, v]) => {
    const rows = Array.from(v.rows.values()).sort((a, b) => a.model.localeCompare(b.model, 'es') || Number(a.fromSet) - Number(b.fromSet) || sizeKey(a.size) - sizeKey(b.size));
    return { color, swatch: v.swatch, rows, total: rows.reduce((s, r) => s + r.qty, 0) };
  }).sort((a, b) => (a.color === NO_COLOR ? 1 : b.color === NO_COLOR ? -1 : b.total - a.total));
}

/** Aplica "cortado" a las piezas indicadas de UN pedido: suma el color a cut_colors y, si ya están
 *  todos los colores del ítem, lo marca is_cut. Devuelve los productos nuevos. */
export function applyCut(products: CutItem[], refs: CutRef[]): CutItem[] {
  const out = products.map(p => ({ ...p }));
  for (const ref of refs) {
    const it = out[ref.idx]; if (!it) continue;
    const colors = Array.from(new Set(piecesOf(it.name || '', 1).map(p => p.color)));
    const done = Array.from(new Set([...(it.cut_colors || []), ref.color]));
    if (colors.every(c => done.includes(c))) { it.is_cut = true; delete it.cut_colors; } else it.cut_colors = done;
  }
  return out;
}
